import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@core/icons";
import { ImageWithPlaceholder } from "@core/media";
import { Shell } from "../access/AccessLayout";
import { BigButton } from "../access/primitives";
import { clearValidatorSession, loadValidatorSession } from "../access/session";
import { signOut } from "../access/api";
import type { CodeToValidate } from "../home/api";
import { SessionHeader } from "../home/SessionHeader";
import "./confirm.css";

// SPEC-303 · Confirmación previa al consumo — `validador.jsx:390-461`
// (`Confirmar`). ANNEX-008/PA-041 (resolución PROTO-01) retira avatar, nivel
// del círculo y vehículo de la tarjeta del miembro: solo el nombre. El
// desglose, el tipo de consumo, los puntos y el tiempo restante siguen el
// prototipo valor por valor.
const MESSAGES = {
  // SPEC-303 §8.
  expired:
    "El código venció mientras revisabas. Pide al miembro que genere uno nuevo",
  cancelled: "El código no fue consumido. Sigue disponible",
};

const CONSUMPTION_LABEL: Record<string, string> = {
  once: "Única vez",
  monthly: "Mensual",
  yearly: "Anual",
};

interface LocationState {
  code?: CodeToValidate;
  entry?: "manual" | "scan";
  problem?: unknown;
}

// RF-VAL-26: el restante se calcula desde `expiresAt` y se refresca cada
// segundo mientras la pantalla está abierta (criterio 6). Quien decide si el
// código venció de verdad es el servidor (ADR-003) — este contador es
// presentación, y llegar a cero acá solo deshabilita el botón y explica qué
// pasó (criterio 7), no cierra la pantalla.
function formatRemaining(ms: number): string {
  if (ms <= 0) return "Código vencido";
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `Vence en ${String(minutes)} min ${String(seconds)} s`;
}

export function ConfirmScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as LocationState | null) ?? null;
  const code = state?.code ?? null;
  const entry = state?.entry;
  // `RequireValidatorSession` (App.tsx) ya garantiza sesión válida antes de
  // montar esta pantalla, pero el tipo de `loadValidatorSession()` sigue
  // siendo `| null` — mismo trato defensivo que `HomeScreen`.
  const session = loadValidatorSession();
  const [signingOut, setSigningOut] = useState(false);

  const [remainingMs, setRemainingMs] = useState(() =>
    code ? new Date(code.expiresAt).getTime() - Date.now() : 0,
  );

  useEffect(() => {
    if (!code) return;
    const expiresAtMs = new Date(code.expiresAt).getTime();
    setRemainingMs(expiresAtMs - Date.now());
    const interval = setInterval(() => {
      setRemainingMs(expiresAtMs - Date.now());
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [code]);

  async function handleSignOut() {
    setSigningOut(true);
    try {
      if (session?.token) {
        await signOut(session.token);
      }
    } catch {
      // RF-VAL-11 / ANNEX-011 §2.3: la salida es local de todos modos — si
      // el servidor no responde, igual se limpia la sesión del dispositivo.
      // Mismo trato que `HomeScreen.handleSignOut`.
    } finally {
      clearValidatorSession();
      navigate("/validator/access-login", { replace: true });
    }
  }

  function handleCancel() {
    // RF-VAL-28 / SPEC-303 §4 y §8: vuelve al inicio informando que el
    // código no fue consumido y sigue disponible. El validador no tiene
    // `ToastProvider` (SPEC-012 es exclusivo del portal, `main.tsx` deja a
    // `ValidatorApp` fuera de ese árbol a propósito): se sigue el patrón de
    // aviso propio de esta superficie — un `Notice` transitorio montado en
    // el destino, como ya hace `ScannerScreen.showTransientNotice` — en vez
    // de inventar un toast nuevo.
    navigate("/validator/home", { state: { notice: MESSAGES.cancelled } });
  }

  function handleConfirm() {
    if (remainingMs <= 0) return;
    // R-01 (SPEC-304): esta pantalla no llama a `POST
    // /v1/validator/redemptions` — lo hace `ResultScreen` al montar. Lo que
    // sí hace acá es generar **una** `Idempotency-Key` y pasarla en el
    // estado de navegación: `ResultScreen` la reutiliza tal cual, inclusive
    // al reintentar tras un corte de conexión (RF-VAL-34) — regenerarla en
    // cada intento rompería la garantía de que un reintento con la misma
    // clave devuelve el resultado original en vez de cobrar dos veces.
    navigate("/validator/result", {
      state: { code, entry, idempotencyKey: crypto.randomUUID() },
    });
  }

  // `state.problem` sin `state.code` (errores de lookup, pantallas de
  // SPEC-304) y la entrada directa a `/validator/confirm` sin estado caen
  // acá por igual: sin un código utilizable no hay nada que confirmar, así
  // que se redirige al inicio — el mismo comportamiento que tenía la ruta
  // provisional que esta pantalla reemplaza (`App.tsx`, comentario SPEC-302).
  if (!code) {
    return <Navigate to="/validator/home" replace />;
  }

  // Defensivo, igual que `HomeScreen`: `RequireValidatorSession` (App.tsx)
  // ya garantiza sesión válida antes de montar esta pantalla.
  if (!session) {
    return null;
  }

  const isReward = code.kind === "reward";
  const expired = remainingMs <= 0;
  const usedInPeriod = code.redemption?.usedInPeriod ?? false;
  const consumptionLabel = code.redemption?.type
    ? CONSUMPTION_LABEL[code.redemption.type]
    : null;

  return (
    <Shell>
      <SessionHeader
        session={session}
        signingOut={signingOut}
        onSignOut={() => {
          void handleSignOut();
        }}
      />
      <div className="sc-confirm-body">
        <div className="sc-confirm-top">
          <span className="sc-confirm-badge">
            <Icon name="qr" size={15} stroke={1.9} />
            Código leído
          </span>
          <span className="sc-confirm-remaining">
            {formatRemaining(remainingMs)}
          </span>
        </div>

        {/* "Confirma", no "Confirme": T7/QA-MTZ-002 §5.4 exige tuteo único,
            sin "usted" — el prototipo (`validador.jsx:403`) usa la forma de
            "usted", pero `test/portal/userMessageHygiene.test.ts` la
            prohíbe en todo el validador. */}
        <h1 className="sc-confirm-title">Confirma antes de aceptar</h1>
        {/* RF-VAL-21: la advertencia va como párrafo plano bajo el
            subtítulo, calcada de `validador.jsx:404` — sin recuadro ni
            ícono. `components.md:183` reserva `Aviso`/`Notice` para login
            con error, escáner denegado y resultados fallidos, no para esta
            pantalla. Sigue siendo lo primero visible sin desplazamiento. */}
        <p
          className={
            expired
              ? "sc-confirm-warning sc-confirm-warning--danger"
              : "sc-confirm-warning"
          }
        >
          {expired ? MESSAGES.expired : code.warning}
        </p>

        {/* ANNEX-008/PA-041: solo el nombre del miembro. Ni fotografía ni
            vehículo — el prototipo (`validador.jsx:406-412`) dibuja avatar +
            nivel + vehículo, y el anexo los retira; el layout se adapta a un
            solo dato. */}
        <div className="sc-confirm-member">
          <div className="sc-confirm-member-name">{code.member.name}</div>
        </div>

        <div className="sc-confirm-item">
          <ImageWithPlaceholder
            src={code.item.imageUrl}
            className="sc-confirm-item-image"
          />
          <div className="sc-confirm-item-content">
            <div className="sc-confirm-item-kind">
              {isReward ? "Recompensa por puntos" : "Beneficio del círculo"}
            </div>
            <div className="sc-confirm-item-title">{code.item.name}</div>
            {/* RF-VAL-23: desglose íntegro, sin recortar y sin "ver más". */}
            <div className="sc-confirm-includes">
              {code.item.includes.map((line) => (
                <div key={line} className="sc-confirm-include">
                  <Icon
                    name="check"
                    size={17}
                    color="var(--gold-2)"
                    stroke={2.2}
                    style={{ flex: "none", marginTop: 1 }}
                  />
                  <span>{line}</span>
                </div>
              ))}
            </div>
            {consumptionLabel ? (
              <div className="sc-confirm-pills">
                <span className="sc-confirm-pill">
                  Consumo: {consumptionLabel}
                </span>
                <span
                  className={
                    usedInPeriod
                      ? "sc-confirm-pill sc-confirm-pill--danger"
                      : "sc-confirm-pill sc-confirm-pill--ok"
                  }
                >
                  {usedInPeriod
                    ? "Ya usado en el período"
                    : "Disponible en el período"}
                </span>
              </div>
            ) : null}
          </div>
        </div>

        {isReward && code.points ? (
          <div className="sc-confirm-points">
            <div className="sc-confirm-points-row">
              <span>Puntos a descontar</span>
              <span className="sc-confirm-points-value">
                −{(code.points.cost ?? 0).toLocaleString("es-EC")}
              </span>
            </div>
            <div className="sc-confirm-points-row sc-confirm-points-row--balance">
              <span>Saldo del miembro</span>
              <span className="sc-confirm-points-balance">
                {(code.points.balanceBefore ?? 0).toLocaleString("es-EC")}{" "}
                <span className="sc-confirm-points-arrow">→</span>{" "}
                <b>{(code.points.balanceAfter ?? 0).toLocaleString("es-EC")}</b>
              </span>
            </div>
          </div>
        ) : null}

        {/* RF-VAL-27 + ANNEX-011: el lugar puede ser comercio aliado o
            taller — se muestra el nombre tal cual llega, sin llamarlo
            "comercio". */}
        <div className="sc-confirm-footer">
          Código {code.publicCode} · {code.place.name}
        </div>
      </div>

      <div className="sc-confirm-actions">
        <BigButton tone="green" disabled={expired} onClick={handleConfirm}>
          <Icon name="check" size={18} stroke={2} />
          Confirmar consumo
        </BigButton>
        <div className="sc-confirm-gap" />
        <BigButton tone="ghost" onClick={handleCancel}>
          Cancelar
        </BigButton>
      </div>
    </Shell>
  );
}
